/**
 * React hook for using the OOP User model
 */

import { useContext } from 'react';
import { useAuth } from '@/context/AuthContext';
import { User } from '@/models/User';
import { UserFactory } from '@/models/UserFactory';
import { BaseRole } from '@/models/roles/BaseRole';
import { PermissionManager } from '@/models/permissions/PermissionManager';
import { useUserPermissions } from '@/models/UserProxy';

/**
 * Hook to access the OOP User model
 */
export function useUserModel(): {
  user: User | null;
  primaryRole: BaseRole | null;
  permissions: ReturnType<typeof useUserPermissions>;
  isLoading: boolean;
  isGuest: boolean;
  isMerchant: boolean;
  isAdmin: boolean;
  isSupport: boolean;
} {
  const { userModel, loading, isGuest } = useAuth();
  
  // Get primary role if user exists
  const primaryRole = userModel ? UserFactory.getPrimaryRole(userModel) : null;
  
  // Get permissions
  const permissions = useUserPermissions(userModel);
  
  return {
    user: userModel,
    primaryRole,
    permissions,
    isLoading: loading,
    isGuest,
    isMerchant: userModel?.isMerchant || false,
    isAdmin: userModel?.isAdmin || false,
    isSupport: userModel?.isSupport || false
  };
}

/**
 * Hook to check specific permissions
 */
export function usePermission(permissionId: string): boolean {
  const { userModel } = useAuth();
  
  if (!userModel) return false;
  
  return PermissionManager.hasPermission(userModel, permissionId);
}

/**
 * Hook to check multiple permissions
 */
export function usePermissions(...permissionIds: string[]): {
  hasAll: boolean;
  hasAny: boolean;
  missing: string[];
} {
  const { userModel } = useAuth();
  
  if (!userModel) {
    return {
      hasAll: false,
      hasAny: false,
      missing: permissionIds
    };
  }
  
  const hasAll = PermissionManager.hasAllPermissions(userModel, ...permissionIds);
  const hasAny = PermissionManager.hasAnyPermission(userModel, ...permissionIds);
  const missing = PermissionManager.getMissingPermissions(userModel, ...permissionIds)
    .map(p => p.id);
  
  return { hasAll, hasAny, missing };
}

/**
 * Hook to get user's role-specific features
 */
export function useUserRole(): {
  roleName: string;
  displayName: string;
  navigationItems: any[];
  quickActions: any[];
  dashboardMetrics: any[];
  canPerformAction: (action: string) => boolean;
} {
  const { user, primaryRole } = useUserModel();
  
  if (!user || !primaryRole) {
    return {
      roleName: 'guest',
      displayName: 'Guest',
      navigationItems: [],
      quickActions: [],
      dashboardMetrics: [],
      canPerformAction: () => false
    };
  }
  
  return {
    roleName: primaryRole.roleName,
    displayName: primaryRole.displayName,
    navigationItems: primaryRole.getNavigationItems(),
    quickActions: primaryRole.getQuickActions(),
    dashboardMetrics: primaryRole.getDashboardMetrics(),
    canPerformAction: (action: string) => primaryRole.canPerformAction(action)
  };
}
